import { Translation, TRANSLOCO_CONFIG, TRANSLOCO_LOADER, translocoConfig, TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { environment } from 'environments/environment';
import { TranslocoHttpLoader } from 'app/core/transloco/transloco.http-loader';

/**
 * Chave usada para lembrar o idioma escolhido pelo usuário entre sessões.
 * O seletor do header (languages.component.ts) escreve nela.
 */
export const LANGUAGE_STORAGE_KEY = 'exploration.language';

/** Idiomas oferecidos pelo seletor do header. */
export const AVAILABLE_LANGUAGES = [
    {
        id: 'en',
        label: 'English'
    },
    {
        id: 'br',
        label: 'Português (BR)'
    },
    {
        id: 'es',
        label: 'Español'
    }
];

const FALLBACK_LANG = 'en';

/**
 * Idioma inicial: o que o usuário escolheu da última vez; senão o do navegador,
 * se for um dos suportados; senão inglês.
 */
export function resolveInitialLang(): string {
    const supported = AVAILABLE_LANGUAGES.map(lang => lang.id);

    let stored: string = null;
    try {
        stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    }
    catch {
        // localStorage indisponível (modo privado/SSR) — segue para o fallback.
    }

    if (supported.includes(stored)) {
        return stored;
    }

    // pt-BR / pt vindos do navegador caem no 'br' do projeto.
    const browserLang = (navigator?.language ?? '').toLowerCase();
    if (browserLang.startsWith('pt')) {
        return 'br';
    }
    if (browserLang.startsWith('es')) {
        return 'es';
    }
    if (browserLang.startsWith('en')) {
        return 'en';
    }

    return FALLBACK_LANG;
}

@NgModule({
    exports  : [
        TranslocoModule
    ],
    providers: [
        {
            // Provide the default Transloco configuration
            provide : TRANSLOCO_CONFIG,
            useValue: translocoConfig({
                availableLangs      : AVAILABLE_LANGUAGES,
                defaultLang         : resolveInitialLang(),
                fallbackLang        : FALLBACK_LANG,
                reRenderOnLangChange: true,
                prodMode            : environment.production
            })
        },
        {
            // Provide the default Transloco loader
            provide : TRANSLOCO_LOADER,
            useClass: TranslocoHttpLoader
        },
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide   : APP_INITIALIZER,
            deps      : [TranslocoService],
            useFactory: (translocoService: TranslocoService): any => (): Promise<Translation> => {
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);
                return translocoService.load(defaultLang).toPromise();
            },
            multi     : true
        }
    ]
})
export class TranslocoCoreModule
{
}
